import React, {FC, ReactNode, useRef} from 'react';
import styled from 'styled-components';

import {computeMultiSelectCheckboxState} from '../../../../../../../../src/components/common/table/helpers/tableHelpers';
import {useAsyncTableSource} from '../../../../../../../../src/components/common/table/helpers/useAsyncTableSource';
import {TableColumn} from '../../../../../../../../src/components/common/table/tableColumn';
import {TableMultiSelectHeaderRow} from '../../../../../../../../src/components/common/table/tableMultiSelectHeaderRow';
import {TableRowRenderOptions} from '../../../../../../../../src/components/common/table/tableRow';
import {
  VirtualTable,
  VirtualTableHandle,
} from '../../../../../../../../src/components/common/table/virtualTable';
import {
  VirtualTableRowRenderer,
  VirtualTableRowSiblingData,
} from '../../../../../../../../src/components/common/table/virtualTableBase';
import {CancelToken} from '../../../../../../../../src/helpers/asyncCancelHelpers';
import {CardGroupModel} from '../../../../../../../../src/models/api/cardGroupModel';
import {TableOffsetPaginationDataModel} from '../../../../../../../../src/models/table/tablePaginationDataModel';
import {ContactsManagerTableStatusesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {KeyboardActionsEnum} from '../../../../../../../shared/helpers/src/browser/keyboardHelpers';
import {
  AsyncHandlerOf,
  Handler,
  HandlerOf,
  HandlerOfWithOptional,
  Renderer,
} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {KeyboardEventHandlers} from '../../../../../../../shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../../../shared/ui/src/components/keyboard/keyboardShortcuts';
import {VisualSizesEnum} from '../../../../../../../shared/ui/src/styles/commonStyles';
import {useContactsManagerContext} from '../../contactsManagerContext';
import {
  getAllTableColumnsInOrder,
  renderContactsTableMultiSelectCardCount,
} from '../common/contacts/contactsTableHelpers';
import {ContactsTableRowContainer} from '../common/contacts/contactsTableRowContainer';
import {ContactsManagerCustomFieldColumnMetadata} from '../common/contactsManagerCustomFieldTypes';
import {SharedColumnsEnum} from '../common/sharedTableConfig';
import {
  computeBulkSelectedValues,
  computeNextSelectedItemId,
  computePreviousSelectedItemId,
} from '../common/tableHelpers';

/*
 * Props.
 */

interface ListContactsTableProps {
  cardIds: ReadonlyArray<number>;
  cardGroup: CardGroupModel;
  multiSelectHeaderActions: ReactNode;
  shouldHideAccountsColumn?: boolean;
  tableStatus?: ContactsManagerTableStatusesEnum;
  isInMultiSelectMode: boolean;
  selectedCardIds: ReadonlyArray<number>;
  paginationData?: TableOffsetPaginationDataModel;
  dynamicColumns: TableColumn<SharedColumnsEnum, ContactsManagerCustomFieldColumnMetadata | undefined>[];
  onFetchTableDataAsync: AsyncHandlerOf<CancelToken>;
  renderEmpty: Renderer;
  onReset: Handler;
  addSelectedCardIds: HandlerOfWithOptional<ReadonlyArray<number>, boolean>;
  removeSelectedCardIds: HandlerOf<ReadonlyArray<number>>;
}

/*
 * Styles.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0 48px 0 0px;
  position: relative;
`;

/*
 * Component.
 */

export const ListContactsTable: FC<ListContactsTableProps> = ({
  cardIds,
  cardGroup,
  multiSelectHeaderActions,
  shouldHideAccountsColumn,
  tableStatus,
  isInMultiSelectMode,
  selectedCardIds,
  dynamicColumns,
  paginationData,
  renderEmpty,
  onFetchTableDataAsync,
  onReset,
  addSelectedCardIds,
  removeSelectedCardIds,
}) => {
  const tableRef = useRef<VirtualTableHandle>();
  const allTableColumns = getAllTableColumnsInOrder(dynamicColumns, shouldHideAccountsColumn);
  const {panelId} = useContactsManagerContext();

  const lastSelectedCardIdRef = useRef<number>();

  const onSelectCardId = (cardId: number) => {
    addSelectedCardIds([cardId]);
    lastSelectedCardIdRef.current = cardId;
  };

  const keyboardHandlers: KeyboardEventHandlers = {
    [KeyboardActionsEnum.MOVE_UP]: () => {
      if (isInMultiSelectMode) {
        return;
      }
      const nextSelectedCardId = computePreviousSelectedItemId(cardIds, selectedCardIds);
      if (nextSelectedCardId) {
        onSelectCardId(nextSelectedCardId);
      }
    },
    [KeyboardActionsEnum.MOVE_DOWN]: () => {
      if (isInMultiSelectMode) {
        return;
      }
      const nextSelectedCardId = computeNextSelectedItemId(cardIds, selectedCardIds);
      if (nextSelectedCardId) {
        onSelectCardId(nextSelectedCardId);
      }
    },
  };
  const source = useAsyncTableSource(
    {
      onReset: async () => onReset(),
      fetcher: async ({cancelToken}) => onFetchTableDataAsync(cancelToken),
    },
    [],
  );

  const renderRow: VirtualTableRowRenderer<number> = (
    cardId: number,
    options: TableRowRenderOptions,
    siblingData?: VirtualTableRowSiblingData<number>,
  ) => (
    <ContactsTableRowContainer
      isSelected={selectedCardIds.includes(cardId)}
      isPreviousSiblingSelected={selectedCardIds.includes(siblingData?.previous ?? -1)}
      isNextSiblingSelected={selectedCardIds.includes(siblingData?.next ?? -1)}
      cardId={cardId}
      panelId={panelId}
      options={options}
      tableColumns={allTableColumns}
      isInMultiSelectMode={isInMultiSelectMode}
      onSelect={() => onSelectCardId(cardId)}
      onMultiSelect={() => {
        addSelectedCardIds(computeBulkSelectedValues(cardId, lastSelectedCardIdRef.current, cardIds), true);
      }}
      onCheckBoxToggle={(shouldSelect) => {
        if (shouldSelect) {
          addSelectedCardIds([cardId], true);
          lastSelectedCardIdRef.current = cardId;
        } else {
          removeSelectedCardIds([cardId]);
        }
      }}
    />
  );

  const renderMultiSelectHeader = (onMultiSelectHeaderChange: HandlerOf<boolean>) => (
    <TableMultiSelectHeaderRow
      onMultiSelectChange={onMultiSelectHeaderChange}
      multiSelectStatus={computeMultiSelectCheckboxState(cardIds.length, selectedCardIds.length)}
      renderTotalItemsLabel={() => renderContactsTableMultiSelectCardCount(selectedCardIds.length)}
    >
      {multiSelectHeaderActions}
    </TableMultiSelectHeaderRow>
  );

  return (
    <StyledTableContainerDiv>
      <KeyboardShortcuts handlers={keyboardHandlers}>
        <VirtualTable<number>
          ref={tableRef}
          columns={allTableColumns}
          source={source}
          items={cardIds}
          size={VisualSizesEnum.SMALL}
          renderRow={renderRow}
          paginationData={paginationData}
          renderEmpty={renderEmpty}
          shouldAllowMultiSelect
          isInMultiSelectMode={isInMultiSelectMode}
          isSourceUnstable
          renderMultiSelectHeader={renderMultiSelectHeader}
          onMultiSelectHeaderChange={(checked) => {
            if (checked) {
              addSelectedCardIds(cardIds);
            } else {
              removeSelectedCardIds(cardIds);
            }
          }}
        />
      </KeyboardShortcuts>
    </StyledTableContainerDiv>
  );
};
